import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../../utils/seo";
import { fetchCategoryTree } from "../../api/categories";
import "./NotFound.css";

/**
 * The page for a URL the app does not route.
 *
 * Before this existed, `src/App.js` declared 45 routes and no wildcard, so an
 * unmatched path rendered the nav and footer around **nothing** — and the SPA
 * catch-all in `public/_redirects` serves index.html with a **200**, so every
 * typo, stale link and crawler guess was an indexable empty page.
 *
 * Two things are deliberately NOT done here:
 *
 * 1. **The edge function is not taught the app's route list.** It would have
 *    to be kept in step with 45 routes forever, and a wrong 404 (a real page
 *    reported missing) is worse than the wrong 200 this replaces. So the
 *    status code stays 200 for an unrouted path and the `noindex` is what
 *    keeps it out of the index. `/category/{unknown}` is the one case the
 *    server CAN decide, because it has the category list — that keeps its
 *    real 404, decided in netlify/edge-functions/seo.js.
 *
 *    The consequence, measured rather than assumed: the static head still
 *    says `index, follow`, and the directive below only lands once React has
 *    run. Googlebot renders JS so the indexing risk is covered; WhatsApp,
 *    Facebook and Slack do not, and see a generic page. That is the price of
 *    not hardcoding the route list in two places, and it is the cheaper of
 *    the two costs.
 * 2. **No redirect to the homepage.** Bouncing someone who mistyped a URL
 *    hides the mistake and loses the back button; saying so and offering
 *    somewhere to go is more use.
 *
 * The categories come from the same `/categories/tree` every other page uses
 * rather than a hardcoded list — a second copy of the slugs is a second thing
 * to get wrong, and this page would be the last place anyone thought to
 * update. On a failed fetch the links simply do not render; the page still
 * works, because Home and All categories are plain links.
 */
const NotFound = () => {
  useSEO({
    title: "Page not found",
    description:
      "This page does not exist on Emporia. Browse our categories to find " +
      "what you were looking for, or head back to the homepage.",
    noindex: true,
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchCategoryTree()
      .then((data) => setCategories((data || []).filter((c) => c.launch_active).slice(0, 8)))
      .catch(() => setCategories([]));
  }, []);

  return (
    <main className="notfound-page">
      <div className="notfound-inner">
        <p className="notfound-code" aria-hidden="true">404</p>
        <h1 className="notfound-title">This page doesn't exist</h1>
        <p className="notfound-lede">
          The link may be out of date, or the address may have a typo in it.
          Nothing is wrong with your account or your orders.
        </p>

        <div className="notfound-actions">
          <Link to="/" className="notfound-btn notfound-btn--primary">Go to the homepage</Link>
          <Link to="/categories" className="notfound-btn">Browse all categories</Link>
        </div>

        {categories.length > 0 && (
          <nav className="notfound-cats" aria-label="Shop by category">
            <p className="notfound-cats-label">Or start from a department</p>
            <ul className="notfound-cats-list">
              {categories.map((c) => (
                <li key={c.slug}>
                  <Link to={`/category/${c.slug}`}>{c.name}</Link>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </main>
  );
};

export default NotFound;
